import { HighlightOptions } from './interfaces';
import { Rectangle } from '../interfaces';
import { HighlightingTypes } from './enums';
import { Core, WebViewerInstance } from '@pdftron/webviewer';



export class TextHighlighter {
    private readonly executionQueue: { func: (...arg) => void, args: any[] }[] = [];
    private isQueueProcessing = false;
    private annotationManager: Core.AnnotationManager; 

    constructor(private readonly viewerInstance: WebViewerInstance) {
        this.annotationManager = this.viewerInstance.Core.documentViewer.getAnnotationManager();
    }

    doHighlight(opts: HighlightOptions, type: HighlightingTypes) {
        if (type === HighlightingTypes.Line) {
            this.executionQueue.push({ func: this.highlightLine, args: [opts] });
        } else if (type === HighlightingTypes.Word) {
            this.executionQueue.push({ func: this.highlightWord, args: [opts] });
        } else if (type === HighlightingTypes.Sentence) {
            this.executionQueue.push({ func: this.highlightSentence, args: [opts] });
        } else if (type === HighlightingTypes.WordBeforeTextNote) {
            this.executionQueue.push({ func: this.highlightWordBeforeTextNote, args: [opts] });
        }

        this.processQueue();
    }

    removeHighlights(annType: HighlightingTypes | null = null) {
        this.executionQueue.push({ func: this.doRemoveHighlights, args: [annType] });
        this.processQueue();
    }

    isWordBeforeTextNoteHighlightExist(): boolean {
        return this.annotationManager.getAnnotationsList().some(x => x.Author === HighlightingTypes.WordBeforeTextNote);
    }

    private processQueue() {
        if (this.isQueueProcessing) {
            return;
        }

        this.isQueueProcessing = true;

        while (this.executionQueue.length > 0) {
            const item = this.executionQueue.shift();
            if (item) {
                item.func.call(this, ...item.args);
            }
        }

        this.isQueueProcessing = false;
    }

    private doRemoveHighlights(annType: HighlightingTypes | null = null) {
        const prevHighlights = this.annotationManager.getAnnotationsList().filter(x =>
            annType === null ?
                (x.Author === HighlightingTypes.Line || x.Author === HighlightingTypes.Sentence || x.Author === HighlightingTypes.Word) :
                x.Author === annType
        );
        this.annotationManager.deleteAnnotations(prevHighlights, { imported: false, force: true });
    }

    private createAnnotation(
        txtRect: Rectangle,
        pageHeight: number,
        pageXOffset: number,
        pageNumber: number,
        color: Core.Annotations.Color,
        highlightType: HighlightingTypes
    ): Core.Annotations.TextHighlightAnnotation {
        const annotation = new this.viewerInstance.Core.Annotations.TextHighlightAnnotation();
        const xLeft = txtRect.x - pageXOffset;
        const xRight = xLeft + txtRect.width;
        const yBottom = pageHeight - txtRect.y;
        const yTop = yBottom - txtRect.height;

        annotation.opacityCap = 1;
        annotation.setPageNumber(pageNumber);
        annotation.setRect(new this.viewerInstance.Core.Math.Rect(xLeft, yTop, xRight, yBottom));
        annotation.Quads.push(new this.viewerInstance.Core.Math.Quad(
            xLeft, yBottom,
            xRight, yBottom,
            xRight, yTop,
            xLeft, yTop
        ));
        annotation.Author = highlightType;
        annotation.FillColor = new this.viewerInstance.Core.Annotations.Color(color.R, color.G, color.B, color.A);
        annotation.StrokeColor = new this.viewerInstance.Core.Annotations.Color(color.R, color.G, color.B, color.A);

        return annotation;
    }

    private highlightSentence(opts: HighlightOptions) {
        this.doRemoveHighlights(HighlightingTypes.Sentence);

        const lines: { rects: Rectangle[], lineRect: Rectangle }[] = [];
        const groupedWords: { rectsGroup: Rectangle[] }[] = [];
        const annotations: Core.Annotations.TextHighlightAnnotation[] = [];

        // grouping words by sentence
        opts.rects.forEach(rect => {
            const lastLine = groupedWords.length === 0 ? null : groupedWords[groupedWords.length - 1];

            if (lastLine === null || lastLine.rectsGroup[0].y !== rect.y) {
                groupedWords.push({ rectsGroup: [rect] });
            } else {
                lastLine.rectsGroup.push(rect);
            }
        });

        // calculatting each sentence boundary
        groupedWords.forEach(group => {
            lines.push({
                rects: group.rectsGroup, lineRect: {
                    x: group.rectsGroup[0].x,
                    y: group.rectsGroup[0].y,
                    height: group.rectsGroup[0].height,
                    width: group.rectsGroup[group.rectsGroup.length - 1].x +
                        group.rectsGroup[group.rectsGroup.length - 1].width -
                        group.rectsGroup[0].x
                }
            });
        });

        lines.forEach(x => {
            const annotation = this.createAnnotation(
                x.lineRect, opts.pageHeight, opts.pageXOffset, opts.pageNumber, opts.color, HighlightingTypes.Sentence
            );

            annotations.push(annotation);
        });

        this.annotationManager.addAnnotations(annotations);
        this.annotationManager.drawAnnotationsFromList(annotations);
    }

    private highlightLine(opts: HighlightOptions) {
        this.doRemoveHighlights(HighlightingTypes.Line);

        const boundary = { ...opts.rects[0] };
        opts.rects.forEach(x => boundary.width += x.width);

        const annotation = this.createAnnotation(
            boundary, opts.pageHeight, opts.pageXOffset, opts.pageNumber, opts.color, HighlightingTypes.Line
        );

        this.annotationManager.addAnnotation(annotation);
        this.annotationManager.drawAnnotationsFromList([annotation]);
    }

    private highlightWord(opts: HighlightOptions) {
        this.doRemoveHighlights(HighlightingTypes.Word);

        const annotation = this.createAnnotation(
            opts.rects[0],
            opts.pageHeight,
            opts.pageXOffset,
            opts.pageNumber,
            opts.color,
            HighlightingTypes.Word
        );

        this.annotationManager.addAnnotation(annotation);
        this.annotationManager.redrawAnnotation(annotation);
    }

    private highlightWordBeforeTextNote(opts: HighlightOptions) {
        this.doRemoveHighlights(HighlightingTypes.WordBeforeTextNote);

        const annotation = this.createAnnotation(
            opts.rects[0],
            opts.pageHeight,
            opts.pageXOffset,
            opts.pageNumber,
            opts.color,
            HighlightingTypes.WordBeforeTextNote
        );

        this.annotationManager.addAnnotation(annotation);
        this.annotationManager.redrawAnnotation(annotation);
    }
}
